import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Monitor, Package, Plus, Smartphone } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyCard,
  TaskyInput,
  TaskySegmentedControl,
  TaskySkeleton,
} from '../tasky';
import { cn } from '../../lib/utils';
import knowledgeApiService from '../../services/api/knowledge/knowledgeApiService';
import type { Product } from '../../types/knowledge';
import easyVideoService, {
  type EasyVideoAnswers,
  type EasyVideoAspectRatio,
  type EasyVideoCreditShortfall,
  type EasyVideoOptions,
  type EasyVideoProject,
} from '../../services/api/easyVideoService';

export type EasyVideoWizardStep = 1 | 2 | 3;

export interface EasyVideoWizardProps {
  /** 作成済みの下書き（再開・「もう1本」）。null なら Q1 から */
  project: EasyVideoProject | null;
  initialStep?: EasyVideoWizardStep;
  /** ログイン中のユーザー ID。商品を選び直したとき、自分の下書きだけを片付ける */
  currentUserId?: number | null;
  /** POST /easy-videos で下書きを作ったとき（URL の project を更新する） */
  onProjectCreated: (project: EasyVideoProject) => void;
  /** start に成功したとき（作成中の画面へ切り替える） */
  onStarted: (project: EasyVideoProject) => void;
}

type AppealChoice = { kind: 'option'; id: string } | { kind: 'free' } | null;

const ASPECT_CHOICES: { value: EasyVideoAspectRatio; title: string; sub: string; icon: React.ReactNode }[] = [
  { value: '9:16', title: 'Instagram・TikTok', sub: '縦長の動画', icon: <Smartphone className="w-6 h-6" /> },
  { value: '16:9', title: 'YouTube・Web', sub: '横長の動画', icon: <Monitor className="w-6 h-6" /> },
];

const DEFAULT_DURATIONS = [15, 20, 30];

/** 選択肢カード（TaskyButton を大きなカードとして使う） */
const choiceClass = (selected: boolean) =>
  cn(
    'h-auto w-full whitespace-normal justify-start text-left rounded-tasky-md border p-4 text-base',
    selected ? 'border-primary bg-primary/10 text-dark' : 'border-warm-border bg-white text-dark hover:bg-warm-hover'
  );

const productImageUrl = (product: Product): string | null => {
  const images = product.product_images ?? [];
  const primary = images.find((img) => img.is_primary) ?? images[0];
  return primary?.url || null;
};

const EasyVideoWizard: React.FC<EasyVideoWizardProps> = ({
  project,
  initialStep,
  currentUserId = null,
  onProjectCreated,
  onStarted,
}) => {
  const [step, setStep] = useState<EasyVideoWizardStep>(initialStep ?? (project ? 2 : 1));
  const [draft, setDraft] = useState<EasyVideoProject | null>(project);

  const [products, setProducts] = useState<Product[] | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [creatingProductId, setCreatingProductId] = useState<number | null>(null);

  const [options, setOptions] = useState<EasyVideoOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const optionsSeq = useRef(0);
  /** 長さを変えた後、次に届く構成案のおすすめを選び直す */
  const repickStructure = useRef(false);
  /** サーバーに保存済みの使う場所（同じ値の PATCH と選択肢の取り直しを省く） */
  const savedAspect = useRef<EasyVideoAspectRatio | null>(project?.aspect_ratio ?? null);

  const [aspect, setAspect] = useState<EasyVideoAspectRatio>(project?.aspect_ratio ?? '9:16');
  const [targetSeconds, setTargetSeconds] = useState<number>(project?.target_seconds ?? 15);
  const [mood, setMood] = useState<string | null>(project?.mood ?? null);
  const [appeal, setAppeal] = useState<AppealChoice>(null);
  const [freeText, setFreeText] = useState('');
  const [structureId, setStructureId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showOtherStructures, setShowOtherStructures] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [shortfall, setShortfall] = useState<EasyVideoCreditShortfall | null>(null);
  const [starting, setStarting] = useState(false);

  // Q1: 登録済み商品（更新日の新しい順）
  useEffect(() => {
    let cancelled = false;
    knowledgeApiService
      .getProducts({ status: 'active' })
      .then((list) => {
        if (cancelled) return;
        const sorted = [...list].sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
        setProducts(sorted);
      })
      .catch(() => {
        if (!cancelled) setProductsError('商品の読み込みに失敗しました');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** 選択肢を取り直す。古い応答は捨てる（回答を変えるたびに呼ぶため） */
  const loadOptions = useCallback(async (projectId: string) => {
    const seq = ++optionsSeq.current;
    setOptionsLoading(true);
    try {
      const next = await easyVideoService.options(projectId);
      if (seq !== optionsSeq.current) return;
      setOptions(next);
    } catch (e) {
      if (seq === optionsSeq.current) setError(easyVideoService.extractErrorMessage(e));
    } finally {
      if (seq === optionsSeq.current) setOptionsLoading(false);
    }
  }, []);

  // 再開時（下書きがある状態で開いた）は選択肢をすぐ取得する
  useEffect(() => {
    if (project) void loadOptions(project.id);
    // 初回のみ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 選択肢が届いたとき・選択が解除されたとき（長さの変更、422 の選び直し）に、未回答の項目をおすすめで埋める
  useEffect(() => {
    if (!options) return;
    setMood((current) => current ?? options.moods.find((m) => m.recommended)?.id ?? options.moods[0]?.id ?? null);
    setAppeal((current) => {
      if (current?.kind === 'free') return current;
      if (current?.kind === 'option' && options.appeals.some((a) => a.id === current.id)) return current;
      const byLabel = draft?.appeal?.label
        ? options.appeals.find((a) => a.label === draft.appeal?.label)
        : undefined;
      const picked = byLabel ?? options.appeals.find((a) => a.recommended) ?? options.appeals[0];
      return picked ? { kind: 'option', id: picked.id } : { kind: 'free' };
    });
    const repick = repickStructure.current;
    repickStructure.current = false;
    setStructureId((current) => {
      if (!repick && current && options.structures.some((s) => s.id === current)) return current;
      return options.structures.find((s) => s.recommended)?.id ?? options.structures[0]?.id ?? null;
    });
    // draft は「前回の回答」を照合するためだけに読む
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, appeal === null, structureId === null, mood === null]);

  /** 回答を保存し、見積もり・構成案に効く項目なら選択肢を取り直す */
  const saveAnswers = useCallback(
    async (answers: EasyVideoAnswers, refreshOptions: boolean) => {
      if (!draft) return;
      try {
        await easyVideoService.update(draft.id, answers);
        if (refreshOptions) await loadOptions(draft.id);
      } catch (e) {
        setOptionsLoading(false);
        setError(easyVideoService.extractErrorMessage(e));
      }
    },
    [draft, loadOptions]
  );

  const handleSelectProduct = async (product: Product) => {
    setError(null);
    if (draft?.product?.id === product.id) {
      setStep(2);
      return;
    }
    setCreatingProductId(product.id);
    try {
      const created = await easyVideoService.create(product.id);
      const ownDraft = currentUserId !== null && draft?.created_by?.id === currentUserId;
      if (draft && draft.status === 'draft' && ownDraft) {
        // 商品を選び直した場合、自分の前の下書きは使わないので片付ける（組織の他の人の下書きは残す。失敗しても続行）
        easyVideoService.remove(draft.id).catch(() => undefined);
      }
      setDraft(created);
      savedAspect.current = created.aspect_ratio ?? null;
      setAspect(created.aspect_ratio ?? aspect);
      setTargetSeconds(created.target_seconds ?? targetSeconds);
      setOptions(null);
      setAppeal(null);
      setStructureId(null);
      onProjectCreated(created);
      setStep(2);
      // Q2・Q3 に答えている間に取得を終える
      void loadOptions(created.id);
    } catch (e) {
      setError(easyVideoService.extractErrorMessage(e));
    } finally {
      setCreatingProductId(null);
    }
  };

  const handleSelectAspect = (value: EasyVideoAspectRatio) => {
    setAspect(value);
    setStep(3);
    if (value === savedAspect.current) return;
    savedAspect.current = value;
    void saveAnswers({ aspect_ratio: value }, true);
  };

  const handleSelectAppeal = (choice: AppealChoice) => {
    setAppeal(choice);
    if (choice?.kind === 'option') void saveAnswers({ appeal_id: choice.id, appeal_free_text: null }, true);
  };

  const handleFreeTextBlur = () => {
    const text = freeText.trim();
    if (appeal?.kind === 'free' && text) void saveAnswers({ appeal_id: null, appeal_free_text: text }, true);
  };

  const handleSelectMood = (value: string) => {
    setMood(value);
    void saveAnswers({ mood: value }, true);
  };

  const handleSelectDuration = (value: string) => {
    const seconds = Number(value);
    setTargetSeconds(seconds);
    // 構成案は長さに依存し、サーバーも選択中の構成を解除するので、取り直した案のおすすめを選び直す。
    // 取得中の古い応答は捨て、取り直しが終わるまで「作る」を止める
    optionsSeq.current += 1;
    repickStructure.current = true;
    setOptionsLoading(true);
    setShowOtherStructures(false);
    void saveAnswers({ target_seconds: seconds }, true);
  };

  const handleSelectStructure = (id: string) => {
    setStructureId(id);
    void saveAnswers({ structure_id: id, structure_free_text: null }, false);
  };

  const freeTextMissing = appeal?.kind === 'free' && !freeText.trim();
  const canStart = !!draft && !!options && !optionsLoading && !!appeal && !freeTextMissing && !starting;

  const handleStart = async () => {
    if (!draft || !appeal) return;
    setStarting(true);
    setError(null);
    setShortfall(null);
    try {
      // 取りこぼしがないよう、開始直前に全回答をまとめて保存する
      await easyVideoService.update(draft.id, {
        aspect_ratio: aspect,
        target_seconds: targetSeconds,
        ...(mood ? { mood } : {}),
        ...(appeal.kind === 'option'
          ? { appeal_id: appeal.id, appeal_free_text: null }
          : { appeal_id: null, appeal_free_text: freeText.trim() }),
        ...(structureId ? { structure_id: structureId, structure_free_text: null } : {}),
      });
      const started = await easyVideoService.start(draft.id);
      onStarted(started);
    } catch (e) {
      const lack = easyVideoService.extractCreditShortfall(e);
      const stale = easyVideoService.extractStaleChoices(e);
      if (lack) {
        setShortfall(lack);
      } else if (stale.length > 0) {
        // 商品の情報が更新されて候補が変わった。取り直して、おすすめから選び直してもらう
        if (stale.includes('appeal_id')) setAppeal(null);
        if (stale.includes('structure_id')) setStructureId(null);
        setError('商品の情報が更新されたため、選択肢を読み込み直しました。内容を確認して、もう一度「この内容で作る」を押してください。');
        void loadOptions(draft.id);
      } else {
        setError(easyVideoService.extractErrorMessage(e));
      }
    } finally {
      setStarting(false);
    }
  };

  const creditsMax = options?.estimate.credits_max;
  const balance = options?.estimate.balance;
  const creditsLabel = creditsMax !== undefined ? `最大 約${creditsMax.toLocaleString()} cr` : '最大 約— cr';

  const recommendedStructure = useMemo(
    () => options?.structures.find((s) => s.recommended) ?? options?.structures[0],
    [options]
  );
  const otherStructures = useMemo(
    () => (options?.structures ?? []).filter((s) => s.id !== recommendedStructure?.id),
    [options, recommendedStructure]
  );
  const durations = options?.durations?.length ? options.durations : DEFAULT_DURATIONS;

  const renderProducts = () => {
    if (productsError) return <TaskyAlert tone="error">{productsError}</TaskyAlert>;
    if (!products) {
      return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="easy-video-products-loading">
          {[0, 1, 2].map((i) => (
            <TaskySkeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {products.map((product) => {
          const selected = draft?.product?.id === product.id;
          const image = productImageUrl(product);
          return (
            <TaskyButton
              key={product.id}
              variant="ghost"
              aria-pressed={selected}
              isLoading={creatingProductId === product.id}
              disabled={creatingProductId !== null && creatingProductId !== product.id}
              onClick={() => handleSelectProduct(product)}
              className={cn(choiceClass(selected), 'flex-col items-stretch gap-2 p-2')}
            >
              <span className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-tasky-sm bg-gray-100">
                {image ? (
                  <img src={image} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Package className="w-8 h-8 text-gray-400" />
                )}
              </span>
              <span className="line-clamp-2 text-sm font-medium">{product.name}</span>
            </TaskyButton>
          );
        })}
        <Link
          to="/knowledge"
          className="flex min-h-40 flex-col items-center justify-center gap-2 rounded-tasky-md border border-dashed border-warm-border p-4 text-center text-sm text-gray-600 hover:bg-warm-hover"
        >
          <Plus className="w-6 h-6" />
          URLから商品を登録
        </Link>
      </div>
    );
  };

  const renderStep = () => {
    if (step === 1) {
      return (
        <section aria-labelledby="easy-video-q1" className="space-y-4">
          <h2 id="easy-video-q1" className="text-lg font-semibold text-dark">
            どの商品の動画を作りますか？
          </h2>
          {renderProducts()}
        </section>
      );
    }

    if (step === 2) {
      return (
        <section aria-labelledby="easy-video-q2" className="space-y-4">
          <h2 id="easy-video-q2" className="text-lg font-semibold text-dark">
            どこで使いますか？
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {ASPECT_CHOICES.map((choice) => (
              <TaskyButton
                key={choice.value}
                variant="ghost"
                aria-pressed={aspect === choice.value}
                onClick={() => handleSelectAspect(choice.value)}
                className={cn(choiceClass(aspect === choice.value), 'gap-3')}
              >
                {choice.icon}
                <span className="flex flex-col">
                  <span className="font-semibold">{choice.title}</span>
                  <span className="text-sm text-gray-500">{choice.sub}</span>
                </span>
              </TaskyButton>
            ))}
          </div>
        </section>
      );
    }

    return (
      <section aria-labelledby="easy-video-q3" className="space-y-4">
        <h2 id="easy-video-q3" className="text-lg font-semibold text-dark">
          いちばん伝えたいことは？
        </h2>
        {!options && optionsLoading ? (
          <div className="space-y-2" data-testid="easy-video-options-loading">
            <p className="text-sm text-gray-500">商品に合う候補を考えています（10秒ほどかかります）…</p>
            {[0, 1, 2].map((i) => (
              <TaskySkeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-2" role="radiogroup" aria-label="いちばん伝えたいこと">
            {(options?.appeals ?? []).slice(0, 3).map((item) => {
              const selected = appeal?.kind === 'option' && appeal.id === item.id;
              return (
                <TaskyButton
                  key={item.id}
                  variant="ghost"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => handleSelectAppeal({ kind: 'option', id: item.id })}
                  className={cn(choiceClass(selected), 'flex-col items-start gap-1')}
                >
                  <span className="flex items-center gap-2 font-medium">
                    {item.label}
                    {item.recommended && <TaskyBadge variant="info">おすすめ</TaskyBadge>}
                  </span>
                  {item.direction && <span className="text-sm text-gray-500">{item.direction}</span>}
                </TaskyButton>
              );
            })}
            <TaskyButton
              variant="ghost"
              role="radio"
              aria-checked={appeal?.kind === 'free'}
              onClick={() => handleSelectAppeal({ kind: 'free' })}
              className={choiceClass(appeal?.kind === 'free')}
            >
              その他（自由入力）
            </TaskyButton>
            {appeal?.kind === 'free' && (
              <TaskyInput
                aria-label="伝えたいこと（自由入力）"
                value={freeText}
                maxLength={200}
                placeholder="例: 忙しい朝でも3分で準備できること"
                onChange={(e) => setFreeText(e.target.value)}
                onBlur={handleFreeTextBlur}
              />
            )}
          </div>
        )}

        {renderSettings()}

        <p className="text-sm text-gray-600">
          含まれるもの: BGM・最後に商品名とロゴのカード／テロップは入りません
        </p>
      </section>
    );
  };

  const renderSettings = () => (
    <TaskyCard variant="warm" padding="sm">
      <TaskyButton
        variant="ghost"
        aria-expanded={showSettings}
        onClick={() => setShowSettings((v) => !v)}
        className="w-full justify-between"
      >
        おまかせ設定を確認・変更
        {showSettings ? <ChevronUp /> : <ChevronDown />}
      </TaskyButton>
      {showSettings && !options && (
        <p className="mt-3 text-sm text-gray-500" data-testid="easy-video-settings-loading">
          おまかせの内容を読み込んでいます…
        </p>
      )}
      {showSettings && options && (
        <div className="mt-3 space-y-5" data-testid="easy-video-settings">
          <div className="space-y-2">
            <p className="text-sm font-medium text-dark">雰囲気</p>
            <div className="flex flex-wrap gap-2">
              {(options?.moods ?? []).map((m) => (
                <TaskyButton
                  key={m.id}
                  size="sm"
                  variant={mood === m.id ? 'default' : 'secondary'}
                  aria-pressed={mood === m.id}
                  onClick={() => handleSelectMood(m.id)}
                >
                  {m.label}
                  {m.recommended && '（おすすめ）'}
                </TaskyButton>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-dark">長さ</p>
            <TaskySegmentedControl
              aria-label="長さ"
              value={String(targetSeconds)}
              onChange={handleSelectDuration}
              options={durations.map((d) => ({ value: String(d), label: `${d}秒` }))}
            />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-dark">構成</p>
            {optionsLoading && <p className="text-xs text-gray-500">構成案を更新しています…</p>}
            {recommendedStructure && renderStructure(recommendedStructure, true)}
            {otherStructures.length > 0 && !showOtherStructures && (
              <TaskyButton variant="link" size="sm" onClick={() => setShowOtherStructures(true)}>
                ほかの案を見る
              </TaskyButton>
            )}
            {showOtherStructures && otherStructures.map((s) => renderStructure(s, false))}
          </div>
        </div>
      )}
    </TaskyCard>
  );

  const renderStructure = (s: EasyVideoOptions['structures'][number], recommended: boolean) => {
    const selected = structureId === s.id;
    return (
      <TaskyButton
        key={s.id}
        variant="ghost"
        aria-pressed={selected}
        onClick={() => handleSelectStructure(s.id)}
        className={cn(choiceClass(selected), 'flex-col items-start gap-1 text-sm')}
      >
        <span className="flex items-center gap-2 font-medium">
          {s.title}
          {recommended && <TaskyBadge variant="info">おすすめ</TaskyBadge>}
        </span>
        <ol className="list-decimal space-y-0.5 pl-5 text-gray-600">
          {s.scenes.map((scene, i) => (
            <li key={i}>
              {scene.summary}（{scene.duration_sec}秒）
            </li>
          ))}
        </ol>
      </TaskyButton>
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col">
      <div className="space-y-5 px-4 pb-6 pt-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-500" data-testid="easy-video-step">
            {step}/3
          </span>
          {draft?.product && step > 1 && (
            <span className="truncate text-sm text-gray-600">商品: {draft.product.name}</span>
          )}
        </div>

        {error && <TaskyAlert tone="error">{error}</TaskyAlert>}
        {shortfall && (
          <TaskyAlert tone="warning" title="クレジットが足りません">
            {shortfall.requiredCredits !== null && shortfall.availableCredits !== null
              ? `この動画には最大 約${shortfall.requiredCredits.toLocaleString()} cr 必要ですが、残りは ${shortfall.availableCredits.toLocaleString()} cr です。`
              : 'この動画を作るにはクレジットが足りません。'}
            クレジットを追加してから、もう一度お試しください（購入は組織のリーダーが行えます）。
          </TaskyAlert>
        )}

        {renderStep()}
      </div>

      <div className="sticky bottom-0 border-t border-warm-border bg-white px-4 py-3">
        <p className="mb-2 text-sm text-gray-600" data-testid="easy-video-estimate">
          {creditsLabel}
          {balance !== undefined && `（残り ${balance.toLocaleString()} cr）`}
        </p>
        <div className="flex gap-2">
          {step > 1 && (
            <TaskyButton variant="secondary" onClick={() => setStep((s) => (s - 1) as EasyVideoWizardStep)}>
              戻る
            </TaskyButton>
          )}
          {step === 2 && (
            <TaskyButton className="flex-1" onClick={() => handleSelectAspect(aspect)}>
              次へ
            </TaskyButton>
          )}
          {step === 3 && (
            <TaskyButton className="flex-1" disabled={!canStart} isLoading={starting} onClick={handleStart}>
              この内容で作る（{creditsLabel}）
            </TaskyButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default EasyVideoWizard;
